import { useNavigate, useSearchParams } from 'react-router-dom'
import { TAXES_STEPS, ahaMomentTaxesStepsOrder } from '../bookkeeping/utils'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  getAllOnboardingSteps,
  getUserOnboardingSteps,
  getUserStepIdByOnboardingStepId,
} from '../../user-onboarding-steps/onboarding.selectors'
import { useCallback, useEffect } from 'react'
import {
  fetchAllOnboardingSteps,
  fetchUserOnboardingStepsIfNeeded,
  markCompletedAtAndStartedAtForSteps,
} from '../../user-onboarding-steps/onboarding.actions'

export const useTaxesSteps = (defaultStep: TAXES_STEPS) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const allOnboardingSteps = useReselector(getAllOnboardingSteps)
  const allUserOnboardingSteps = useReselector(getUserOnboardingSteps)

  // Get the current step from the URL or use the default step as fallback
  const currentStepString = searchParams.get('step') || defaultStep
  const currentStep = Object.values(TAXES_STEPS).includes(
    currentStepString as TAXES_STEPS
  )
    ? (currentStepString as TAXES_STEPS)
    : defaultStep

  const currentStepIdentifier = Object.values(allOnboardingSteps).find(
    (step) => step.identifier.toLowerCase() === currentStep
  )?.identifier

  const currentStepId = useReselector(
    getUserStepIdByOnboardingStepId,
    currentStepIdentifier
  )

  const isStepCompleted = currentStepId
    ? Boolean(allUserOnboardingSteps[currentStepId]?.completedAt)
    : false

  const nextStep =
    ahaMomentTaxesStepsOrder[ahaMomentTaxesStepsOrder.indexOf(currentStep) + 1]

  const nextStepIdentifier = Object.values(allOnboardingSteps).find(
    (step) => step.identifier.toLowerCase() === nextStep
  )?.identifier

  const nextStepId = useReselector(
    getUserStepIdByOnboardingStepId,
    nextStepIdentifier
  )

  useEffect(() => {
    dispatch(fetchUserOnboardingStepsIfNeeded())
    dispatch(fetchAllOnboardingSteps())
  }, [dispatch])

  const goToStep = useCallback(
    (newStep: 1 | -1 | TAXES_STEPS) => {
      if (typeof newStep === 'string') {
        setSearchParams({ step: newStep })
        return
      }

      const currentStepIndex = ahaMomentTaxesStepsOrder.indexOf(currentStep)

      if (
        currentStepIndex + newStep >= 0 &&
        currentStepIndex + newStep < ahaMomentTaxesStepsOrder.length
      ) {
        setSearchParams({
          step: ahaMomentTaxesStepsOrder[currentStepIndex + newStep],
        })
      }
    },
    [currentStep, setSearchParams]
  )

  const markCompletedAtAndStartedAtForEachStep = useCallback(async () => {
    if (currentStepId) {
      if (!isStepCompleted) {
        await markCompletedAtAndStartedAtForSteps({
          currentStepId,
          nextStepId,
        })(dispatch)
      }
    }
  }, [currentStepId, nextStepId, dispatch, isStepCompleted])

  const goToNextStep = useCallback(async () => {
    try {
      await markCompletedAtAndStartedAtForEachStep()

      if (currentStep === TAXES_STEPS.taxesSuccess) {
        navigate('/dashboard')
      } else {
        goToStep(1)
      }
    } catch (error) {
      console.error('Failed to complete step:', error)
    }
  }, [currentStep, navigate, goToStep, markCompletedAtAndStartedAtForEachStep])

  const goToPreviousStep = useCallback(() => {
    if (currentStep === TAXES_STEPS.learnAboutTaxesAtHeard) {
      navigate('/dashboard')
    } else {
      goToStep(-1)
    }
  }, [goToStep, currentStep, navigate])

  return { currentStep, goToNextStep, goToPreviousStep }
}
